import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { expenseAPI, incomeAPI, userAPI } from '../services/api';
import { Expense, Income, ViewType, FinancialSummary, CreateExpenseRequest, UpdateExpenseRequest, CreateIncomeRequest, IncomeType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { useCategories, getCategoryIcon } from '../contexts/CategoryContext';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subDays } from 'date-fns';
import ExpenseModal from '../components/ExpenseModal';
import {
  BanknotesIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  PlusIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
  BuildingLibraryIcon,
  CheckCircleIcon,
  EyeIcon,
  TagIcon,
  UserGroupIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { ChartPieIcon } from '@heroicons/react/24/solid';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
      when: "beforeChildren" as const
    },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3 }
  },
};

type ReportType = 'charts' | 'grid' | 'summary';
type DateRangePreset = 'today' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'custom' | 'all';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { formatAmount } = useCurrency();
  const { categories } = useCategories();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummary | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [viewType, setViewType] = useState<ViewType>('monthly');
  const [reportType, setReportType] = useState<ReportType>('charts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showAddIncomeModal, setShowAddIncomeModal] = useState(false);



  // Income form state
  const [incomeFormData, setIncomeFormData] = useState<CreateIncomeRequest>({
    amount: 0,
    income_type: 'cash' as IncomeType,
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Filter states
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('thisMonth');
  const [filters, setFilters] = useState({
    selectedUser: 'all',
    category: 'all',
    year: new Date().getFullYear().toString(),
    minAmount: '',
    maxAmount: '',
    dateFrom: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    dateTo: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });

  const loadData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Loading dashboard data for user:', currentUser.name);
      console.log('Selected user filter:', filters.selectedUser);

      const [analyticsData, expensesData, incomesData, financialData, usersData] = await Promise.all([
        expenseAPI.getAnalytics(filters.selectedUser === 'all' ? undefined : filters.selectedUser),
        expenseAPI.getAll(), // Get all expenses for dashboard viewing
        incomeAPI.getAll(), // Get all incomes for dashboard viewing
        incomeAPI.getFinancialSummary(filters.selectedUser === 'all' ? undefined : filters.selectedUser),
        userAPI.getAll()
      ]);

      console.log('Analytics data:', analyticsData);
      console.log('Expenses data:', expensesData);
      console.log('Incomes data:', incomesData);
      console.log('Financial data:', financialData);
      console.log('Users data:', usersData);

      // setAnalytics(analyticsData);
      setExpenses(expensesData);
      setIncomes(incomesData);
      setFinancialSummary(financialData);
      setUsers(usersData);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, filters.selectedUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle date range preset changes
  const handleDateRangePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    const today = new Date();
    let dateFrom = '';
    let dateTo = '';

    switch (preset) {
      case 'today':
        dateFrom = format(today, 'yyyy-MM-dd');
        dateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'last7days':
        dateFrom = format(subDays(today, 7), 'yyyy-MM-dd');
        dateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'last30days':
        dateFrom = format(subDays(today, 30), 'yyyy-MM-dd');
        dateTo = format(today, 'yyyy-MM-dd');
        break;
      case 'thisMonth':
        dateFrom = format(startOfMonth(today), 'yyyy-MM-dd');
        dateTo = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        dateFrom = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        dateTo = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case 'thisYear':
        dateFrom = format(startOfYear(today), 'yyyy-MM-dd');
        dateTo = format(endOfYear(today), 'yyyy-MM-dd');
        break;
      case 'lastYear':
        const lastYear = new Date(today.getFullYear() - 1, 0, 1);
        dateFrom = format(startOfYear(lastYear), 'yyyy-MM-dd');
        dateTo = format(endOfYear(lastYear), 'yyyy-MM-dd');
        break;
      case 'all':
        dateFrom = '';
        dateTo = '';
        break;
      case 'custom':
        // Keep current values for custom
        return;
    }

    setFilters(prev => ({ ...prev, dateFrom, dateTo }));
  };

  // Handle expense form submission
  const handleAddExpense = async (data: CreateExpenseRequest | UpdateExpenseRequest) => {
    if (!currentUser) return;

    await expenseAPI.create({
      ...data,
      user_id: currentUser.id,
    } as CreateExpenseRequest);
    loadData();
  };

  // Handle income form submission
  const handleAddIncome = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      await incomeAPI.create({
        ...incomeFormData,
        date: new Date(incomeFormData.date!).toISOString(),
      });
      setShowAddIncomeModal(false);
      setIncomeFormData({
        amount: 0,
        income_type: 'cash',
        description: '',
        date: new Date().toISOString().split('T')[0],
      });
      loadData();
    } catch (err) {
      setError('Failed to create income');
      console.error('Create income error:', err);
    }
  };

  // Filter expenses based on current filters
  const filteredExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.date);
    const expenseYear = expenseDate.getFullYear().toString();

    // User filter
    if (filters.selectedUser !== 'all' && expense.user_id !== filters.selectedUser) {
      return false;
    }

    // Category filter
    if (filters.category !== 'all' && expense.category !== filters.category) {
      return false;
    }

    // Year filter
    if (filters.year !== 'all' && expenseYear !== filters.year) {
      return false;
    }

    // Amount filters
    if (filters.minAmount && expense.amount < parseFloat(filters.minAmount)) {
      return false;
    }
    if (filters.maxAmount && expense.amount > parseFloat(filters.maxAmount)) {
      return false;
    }

    // Date range filters
    if (filters.dateFrom && expenseDate < new Date(filters.dateFrom)) {
      return false;
    }
    if (filters.dateTo && expenseDate > new Date(filters.dateTo)) {
      return false;
    }

    return true;
  });

  // Get available years from expenses
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availableYears = Array.from(new Set(expenses.map(expense =>
    new Date(expense.date).getFullYear().toString()
  ))).sort().reverse();

  // Get user name helper
  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  const getCategoryChartData = () => {
    // Calculate category data from filtered expenses
    const categoryData: { [key: string]: number } = {};
    filteredExpenses.forEach(expense => {
      categoryData[expense.category] = (categoryData[expense.category] || 0) + expense.amount;
    });

    const categories = Object.keys(categoryData);
    const amounts = Object.values(categoryData);

    if (categories.length === 0) return null;

    return {
      labels: categories,
      datasets: [
        {
          label: 'Amount Spent',
          data: amounts,
          backgroundColor: [
            '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const getTimeChartData = () => {
    // Calculate time-based data from filtered expenses
    const timeData: { [key: string]: number } = {};

    filteredExpenses.forEach(expense => {
      const date = new Date(expense.date);
      let key: string;

      if (viewType === 'monthly') {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else if (viewType === 'yearly') {
        key = date.getFullYear().toString();
      } else { // daily
        key = date.toISOString().split('T')[0];
      }

      timeData[key] = (timeData[key] || 0) + expense.amount;
    });

    const sortedKeys = Object.keys(timeData).sort();
    const amounts = sortedKeys.map(key => timeData[key]);

    if (sortedKeys.length === 0) return null;

    return {
      labels: sortedKeys,
      datasets: [
        {
          label: 'Total Amount',
          data: amounts,
          backgroundColor: '#3B82F6',
          borderColor: '#1D4ED8',
          borderWidth: 1,
        },
      ],
    };
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col justify-center items-center h-64 gap-4"
      >
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center animate-pulse">
          <ChartBarIcon className="h-8 w-8 text-white" />
        </div>
        <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="backdrop-blur-xl bg-red-50/80 dark:bg-red-900/20 border border-red-200/50 dark:border-red-700/50 rounded-2xl p-6"
      >
        <div className="text-red-800 dark:text-red-200 font-medium">{error}</div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={loadData}
          className="mt-4 px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl font-medium shadow-lg shadow-red-500/30 hover:shadow-xl transition-shadow"
        >
          Retry
        </motion.button>
      </motion.div>
    );
  }

  const renderFinancialSummary = () => {
    if (!financialSummary) return null;

    return (
      <motion.div
        variants={fadeInUp}
        initial="hidden"
        animate="visible"
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-xl"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <CurrencyDollarIcon className="h-5 w-5 text-white" />
            </div>
            Financial Overview
          </h2>
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddIncomeModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl text-sm font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-shadow"
            >
              <PlusIcon className="h-4 w-4" />
              Income
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowAddExpenseModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-medium shadow-lg shadow-rose-500/30 hover:shadow-xl transition-shadow"
            >
              <PlusIcon className="h-4 w-4" />
              Expense
            </motion.button>
          </div>
        </div>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {/* Total Summary */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={scaleIn}
            whileHover={{ scale: 1.02, y: -4 }}
            className="relative overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 p-5 rounded-2xl shadow-lg shadow-indigo-500/30"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <CurrencyDollarIcon className="h-5 w-5 text-white/80" />
                <h3 className="text-sm font-medium text-white/80">Total Balance</h3>
              </div>
              <div className={`text-3xl font-bold mb-3 ${financialSummary.remaining_balance >= 0 ? 'text-white' : 'text-red-200'}`}>
                {formatAmount(financialSummary.remaining_balance)}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200">+{formatAmount(financialSummary.total_income)}</span>
                <span className="text-rose-200">-{formatAmount(financialSummary.total_expenses)}</span>
              </div>
            </div>
          </motion.div>

          {/* Bank Summary */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={scaleIn}
            whileHover={{ scale: 1.02, y: -4 }}
            className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 p-5 rounded-2xl shadow-lg shadow-emerald-500/30"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <BuildingLibraryIcon className="h-5 w-5 text-white/80" />
                <h3 className="text-sm font-medium text-white/80">Bank Account</h3>
              </div>
              <div className={`text-3xl font-bold mb-3 ${financialSummary.bank_balance >= 0 ? 'text-white' : 'text-red-200'}`}>
                {formatAmount(financialSummary.bank_balance)}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200">+{formatAmount(financialSummary.bank_income)}</span>
                <span className="text-rose-200">-{formatAmount(financialSummary.bank_expenses)}</span>
              </div>
            </div>
          </motion.div>

          {/* Cash Summary */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={scaleIn}
            whileHover={{ scale: 1.02, y: -4 }}
            className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-600 p-5 rounded-2xl shadow-lg shadow-amber-500/30"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <BanknotesIcon className="h-5 w-5 text-white/80" />
                <h3 className="text-sm font-medium text-white/80">Cash</h3>
              </div>
              <div className={`text-3xl font-bold mb-3 ${financialSummary.cash_balance >= 0 ? 'text-white' : 'text-red-200'}`}>
                {formatAmount(financialSummary.cash_balance)}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200">+{formatAmount(financialSummary.cash_income)}</span>
                <span className="text-rose-200">-{formatAmount(financialSummary.cash_expenses)}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Financial Summary */}
      {renderFinancialSummary()}

      {/* Filters & View Controls */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <FunnelIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white">Filters & Reports</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Customize your view</p>
            </div>
          </div>

          {/* View Type Buttons */}
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'charts', label: 'Charts', icon: ChartBarIcon },
              { value: 'grid', label: 'Grid', icon: TableCellsIcon },
              { value: 'summary', label: 'Summary', icon: PresentationChartLineIcon },
            ].map((type) => (
              <motion.button
                key={type.value}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setReportType(type.value as ReportType)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  reportType === type.value
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Filters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">User</label>
            <select
              value={filters.selectedUser}
              onChange={(e) => setFilters({ ...filters, selectedUser: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="all">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {getCategoryIcon(cat)} {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Date Range</label>
            <select
              value={dateRangePreset}
              onChange={(e) => handleDateRangePresetChange(e.target.value as DateRangePreset)}
              className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="today">Today</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="thisYear">This Year</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom Range</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Time View</label>
            <select
              value={viewType}
              onChange={(e) => setViewType(e.target.value as ViewType)}
              className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          {dateRangePreset === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
            </>
          )}

          <div className="flex items-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setDateRangePreset('thisMonth');
                handleDateRangePresetChange('thisMonth');
                setFilters({
                  selectedUser: 'all',
                  category: 'all',
                  year: new Date().getFullYear().toString(),
                  minAmount: '',
                  maxAmount: '',
                  dateFrom: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
                  dateTo: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
                });
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium transition-all"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {[
          { label: 'Total Spent', value: formatAmount(filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0)), icon: CurrencyDollarIcon, gradient: 'from-rose-500 to-pink-500', shadow: 'shadow-rose-500/30' },
          { label: 'Transactions', value: filteredExpenses.length.toString(), icon: CreditCardIcon, gradient: 'from-blue-500 to-cyan-500', shadow: 'shadow-blue-500/30' },
          { label: 'Categories', value: Array.from(new Set(filteredExpenses.map(exp => exp.category))).length.toString(), icon: ChartBarIcon, gradient: 'from-purple-500 to-indigo-500', shadow: 'shadow-purple-500/30' },
          { label: 'Avg/Day', value: filteredExpenses.length > 0 ? formatAmount(filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0) / Math.max(1, Array.from(new Set(filteredExpenses.map(exp => exp.date.split('T')[0]))).length)) : formatAmount(0), icon: PresentationChartLineIcon, gradient: 'from-amber-500 to-orange-500', shadow: 'shadow-amber-500/30' },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            initial="hidden"
            animate="visible"
            variants={scaleIn}
            whileHover={{ scale: 1.02, y: -4 }}
            className={`backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-5 border border-gray-200/50 dark:border-gray-700/50 shadow-lg`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg ${stat.shadow}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Report Views */}
      <AnimatePresence mode="wait">
        {reportType === 'charts' && (
          <motion.div
            key="charts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Category Chart */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <ChartPieIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Expenses by Category</h3>
              </div>
              <div className="h-72">
                {getCategoryChartData() ? (
                  <Doughnut
                    data={getCategoryChartData()!}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '60%',
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            padding: 20,
                            usePointStyle: true,
                            pointStyle: 'circle',
                          },
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          cornerRadius: 12,
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed;
                              return `${context.label}: ${formatAmount(value)}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ChartPieIcon className="h-12 w-12 mb-2 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Time Chart */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <ChartBarIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Expenses Over Time ({viewType.charAt(0).toUpperCase() + viewType.slice(1)})
                </h3>
              </div>
              <div className="h-72">
                {getTimeChartData() ? (
                  <Bar
                    data={getTimeChartData()!}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 12,
                          cornerRadius: 12,
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              return `Amount: ${formatAmount(value)}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(0, 0, 0, 0.05)',
                          },
                          ticks: {
                            callback: (value) => {
                              return formatAmount(Number(value));
                            },
                          },
                        },
                        x: {
                          grid: {
                            display: false,
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <ChartBarIcon className="h-12 w-12 mb-2 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Grid View */}
        {reportType === 'grid' && (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <TableCellsIcon className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Expense Details</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50/80 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Owner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {filteredExpenses.map((expense, index) => (
                    <motion.tr
                      key={expense.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className={`hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors ${expense.user_id === currentUser?.id ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {getUserName(expense.user_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                        {formatAmount(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 text-indigo-700 dark:text-indigo-300">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                        {expense.description || <span className="text-gray-400 italic">No description</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {format(new Date(expense.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {expense.user_id === currentUser?.id ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/50 text-emerald-700 dark:text-emerald-300">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            Yours
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            <EyeIcon className="h-3.5 w-3.5" />
                            View
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {filteredExpenses.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <TableCellsIcon className="h-12 w-12 mb-3 opacity-50" />
                  <p>No expenses found matching the selected filters.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Summary View */}
        {reportType === 'summary' && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Category Summary */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <TagIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Category Breakdown</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(
                  filteredExpenses.reduce((acc: { [key: string]: { amount: number; count: number } }, expense) => {
                    if (!acc[expense.category]) {
                      acc[expense.category] = { amount: 0, count: 0 };
                    }
                    acc[expense.category].amount += expense.amount;
                    acc[expense.category].count += 1;
                    return acc;
                  }, {})
                )
                  .sort(([,a], [,b]) => b.amount - a.amount)
                  .map(([category, data], index) => {
                    const total = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
                    const percentage = total > 0 ? (data.amount / total) * 100 : 0;
                    return (
                      <motion.div
                        key={category}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="relative overflow-hidden p-4 bg-gray-50/80 dark:bg-gray-700/50 rounded-xl"
                      >
                        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 dark:from-indigo-500/30 dark:to-purple-500/30" style={{ width: `${percentage}%` }} />
                        <div className="relative flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">{category}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{data.count} expenses • {percentage.toFixed(1)}%</div>
                          </div>
                          <div className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatAmount(data.amount)}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            </motion.div>

            {/* User Summary */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <UserGroupIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">User Breakdown</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(
                  filteredExpenses.reduce((acc: { [key: string]: { amount: number; count: number } }, expense) => {
                    const userName = getUserName(expense.user_id);
                    if (!acc[userName]) {
                      acc[userName] = { amount: 0, count: 0 };
                    }
                    acc[userName].amount += expense.amount;
                    acc[userName].count += 1;
                    return acc;
                  }, {})
                )
                  .sort(([,a], [,b]) => b.amount - a.amount)
                  .map(([userName, data], index) => {
                    const total = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
                    const percentage = total > 0 ? (data.amount / total) * 100 : 0;
                    return (
                      <motion.div
                        key={userName}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="relative overflow-hidden p-4 bg-gray-50/80 dark:bg-gray-700/50 rounded-xl"
                      >
                        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 dark:from-blue-500/30 dark:to-cyan-500/30" style={{ width: `${percentage}%` }} />
                        <div className="relative flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                              {userName}
                              {userName === currentUser?.name && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300">You</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{data.count} expenses • {percentage.toFixed(1)}%</div>
                          </div>
                          <div className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatAmount(data.amount)}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Expenses Section - Always Visible */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <TableCellsIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Recent Expenses</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Showing {Math.min(10, filteredExpenses.length)} of {filteredExpenses.length} expenses</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/80 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Description</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Owner</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {filteredExpenses.slice(0, 10).map((expense, index) => (
                <motion.tr
                  key={expense.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className={`hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors ${expense.user_id === currentUser?.id ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {getUserName(expense.user_id)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                    {formatAmount(expense.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 text-indigo-700 dark:text-indigo-300">
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                    {expense.description || <span className="text-gray-400 italic">No description</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {format(new Date(expense.date), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {expense.user_id === currentUser?.id ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/50 text-emerald-700 dark:text-emerald-300">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Yours
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        <EyeIcon className="h-3.5 w-3.5" />
                        View
                      </span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {filteredExpenses.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <TableCellsIcon className="h-12 w-12 mb-3 opacity-50" />
              <p>No expenses found matching the selected filters.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Add Expense Modal */}
      <ExpenseModal
        isOpen={showAddExpenseModal}
        onClose={() => setShowAddExpenseModal(false)}
        onSubmit={handleAddExpense}
        userId={currentUser?.id || ''}
      />

      {/* Add Income Modal */}
      <AnimatePresence>
        {showAddIncomeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddIncomeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-200/50 dark:border-gray-700/50"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <PlusCircleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Income</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Record a new income</p>
                </div>
              </div>
              <form onSubmit={handleAddIncome} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={incomeFormData.amount}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Income Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'cash', label: 'Cash', icon: BanknotesIcon },
                      { value: 'bank', label: 'Bank', icon: BuildingLibraryIcon },
                    ].map((type) => (
                      <motion.button
                        key={type.value}
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIncomeFormData({ ...incomeFormData, income_type: type.value as IncomeType })}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                          incomeFormData.income_type === type.value
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <type.icon className="h-5 w-5" />
                        {type.label}
                      </motion.button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <input
                    type="text"
                    value={incomeFormData.description}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="What was this income from?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                  <input
                    type="date"
                    value={incomeFormData.date}
                    onChange={(e) => setIncomeFormData({ ...incomeFormData, date: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddIncomeModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-all"
                  >
                    Add Income
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Dashboard;
